<template>
  <div class="electorate-details">
    <form class="search" id="search" v-on:submit.prevent="selectAddress">
      <input
        type="text"
        name="address"
        placeholder="Use your postcode to locate threatened wildlife near you"
        id="address"
        v-model="address"
        class="form-control"
        @focus="logSearchBoxFocus"
      />
      <button type="button" class="icon" @click="selectAddress">
        <i class="fal fa-search"></i>
      </button>
    </form>

    <div class="wrapper">
      <h4 class="electorate-name">In {{ electorateName === "" ? "Australian neighbourhood" : "your local area" }}</h4>
      <div class="content">
        <div class="details">
          <div class="section species">
            <div class="count">
              <p>{{ speciesCount }}</p>
            </div>
            <div class="text">
              <p class="uppercase">
                Animals and plants threatened with extinction
              </p>
            </div>
          </div>

          <div class="separator">
            <div class="bar"></div>
          </div>

          <div class="section middle">
            <div class="section-content">
              <div class="icon grade" :style="bgColor">
                <p class="grade">{{ electorateGrade }}</p>
              </div>
              <div class="text">
                <p class="uppercase" v-if="electorateName === ''">
                  Threatened species grade in your local area
                </p>
                <p class="uppercase" v-if="electorateName !== ''">
                  Species recovery grade in your local area
                </p>
              </div>
            </div>
          </div>
          <div class="separator end">
            <div class="bar"></div>
          </div>
        </div>

        <div class="section btn">
          <button
            type="button"
            name="dismiss"
            class="uppercase"
            @click="makeDifference"
          >
            <span v-if="electorateName !== ''">add your voice</span>
            <span v-else>Send a message</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ElectorateDetails",

  data() {
    return {
      address: "",
      speciesTotalCount: "1800+",
      gradeColor: "#757582",
    };
  },
  computed: {
    bgColor() {
      return {
        "--bg-color": this.gradeColor,
      };
    },

    electorateGrade() {
      return this.$store.getters.electorateGrade
        ? this.$store.getters.electorateGrade
        : "F";
    },

    speciesCount() {
      return this.$store.getters.speciesData.speciesCount
        ? this.$store.getters.speciesData.speciesCount
        : this.speciesTotalCount;
    },

    electorateName() {
      return this.$store.getters.electorateName;
    },

    ...mapGetters(["electorateName", "openModal", "modalType"]),
  },

  watch: {
    electorateGrade(newValue) {
      this.updateGradeColor(newValue);
    },
  },

  props: {
    gradesPopupLink: {
      type: String,
      default: "https://www.wwf.org.au/",
    },
  },

  async beforeMount() {
    this.updateGradeColor(this.electorateGrade);
    // const allSpecies = await this.$store.dispatch("getDistinctSpecies");
    // this.speciesTotalCount = allSpecies.length;
  },

  methods: {
    async selectAddress() {
      // Close potentially opened popup & modal
      this.$store.commit("updateOpenInformationPopup", false);
      this.$store.commit("closeModal");

      // Log intro close on first search
      if (this.openModal && this.modalType === "postcodeInfo") {
        this.$store.dispatch("logSimpleEvent", {
          eventName: "Intro Panel Auto Close on Search",
          eventProperties: { "Close Panel": true },
        });
      }

      // Search logic
      const page = window.top.location.href;
      if (!this.address || this.address.length < 4) {
        this.$store.commit("openModal", "invalidAddressError");
        // Log clicked
        this.$store.dispatch("logSimpleEvent", {
          eventName: "Search Button Clicked",
          eventProperties: {
            "source page": page,
            "invalid search": true,
            "search input": "",
          },
        });
      } else {
        try {
          let postcode = this.address;

          // Search for address if not a postcode
          if (!Number.isInteger(this.address) && this.address.length !== 4) {
            const coordinates = await this.$store.dispatch(
              "geocodeAddress",
              this.address
            );
            this.$store.commit("updateAddress", this.address);
            this.$store.commit("updateAddressCoordinates", coordinates);
            postcode = await this.$store.dispatch("getPostcode", coordinates);
          }

          // Update electorate
          const electorates = await this.$store.dispatch(
            "getElectorates",
            postcode
          );
          this.$store.commit(
            "updateElectorateName",
            electorates[0].electoraldivisionname
          );

          // Update electorate Grade
          const electorateGrade = await this.$store.dispatch(
            "getElectoratesGrade",
            [electorates[0].electoraldivisionname]
          );
          this.$store.commit(
            "updateElectorateGrade",
            electorateGrade[0].overallgrade
          );

          // Get drawer data
          const [speciesData, otherSpecies] = await Promise.all([
            this.$store.dispatch("getHeroSpecies", {
              electorate: electorates[0].electoraldivisionname,
              ishero: true,
            }),
            this.$store.dispatch("getSpecies", {
              electoraldivisionname: electorates[0].electoraldivisionname,
              isfeatured: true,
            }),
          ]);

          speciesData.speciesCount =
            electorateGrade[0].numberofthreatenedspeciesinelectorate;
          this.$store.commit("updateSpeciesData", speciesData);

          // Update otheer species store
          this.$store.commit("updateOtherSpecies", otherSpecies);

          // Scroll to species details
          document.getElementById("species-details").scrollIntoView({
            behavior: "smooth",
          });

          // Open drawer
          if (
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent
            )
          ) {
            this.$store.commit("updateOpenDrawer", false);
          } else {
            this.$store.commit("updateOpenDrawer", true);
          }

          // Log clicked
          this.$store.dispatch("logSimpleEvent", {
            eventName: "Search Button Clicked",
            eventProperties: {
              "source page": page,
              "invalid search": false,
              "search input": this.address,
            },
          });

          this.address = null;
        } catch (e) {
          // Log clicked
          this.$store.dispatch("logSimpleEvent", {
            eventName: "Search Button Clicked",
            eventProperties: {
              "source page": page,
              "invalid search": true,
              "search input": this.address,
            },
          });

          // Error modal
          this.$store.commit("openModal", "addressNotFoundError");
          this.address = null;
        }
      }
    },

    makeDifference() {
      if (!this.electorateName) {
        this.$store.commit("openModal", "noElectorateError");
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        document.getElementById("form-top").scrollIntoView({
          behavior: "smooth",
        });
      }

      // Log make diff btn press
      this.$store.dispatch("logSimpleEvent", {
        eventName: "Make a Difference Button",
        eventProperties: {
          Click: true,
          "Map State": this.electorateName,
        },
      });
    },

    updateGradeColor(grade) {
      switch (grade) {
        case "A":
          this.gradeColor = "#319362";
          break;
        case "B":
          this.gradeColor = "#87D178";
          break;
        case "C":
          this.gradeColor = "#EFBB3A";
          break;
        case "D":
          this.gradeColor = "#EA6647";
          break;
        case "E":
          this.gradeColor = "#AE3B49";
          break;
        case "F":
          this.gradeColor = "#733B3F";
          break;
        default:
          this.gradeColor = "#757582";
          break;
      }
    },

    openInformationPopup() {
      this.$store.commit("updatePopupTitle", "What is a recovery grade?");
      this.$store.commit(
        "updatePopupDescription",
        `The recovery grade is an indication of how well the threatened plants and animals in an area are doing in terms of their protection, funding, planning, and threat status trajectory. An ‘A’ is the best possible score, and ‘F’ is the worst. To read the technical paper and learn more about how the grades were calculated <a href='${this.gradesPopupLink}' target='_blank'>click here</a>.`
      );
      this.$store.commit("updateOpenInformationPopup", true);

      document.getElementById("mby-top").scrollIntoView({
        behavior: "smooth",
        block: "end",
      });

      // Log info popup
      this.$store.dispatch("logSimpleEvent", {
        eventName: "Info Pop Up",
        eventProperties: { "Related Content Text": "Recovery Grade" },
      });
    },

    learnMore() {
      document.getElementsByClassName("mby-species-details")[0].scrollIntoView({
        behavior: "smooth",
      });

      // Log learn more
      this.$store.dispatch("logSimpleEvent", {
        eventName: "Search Panel Learn More",
        eventProperties: { "Jump to content": true },
      });
    },

    logSearchBoxFocus() {
      this.$store.dispatch("logSimpleEvent", {
        eventName: "Search Box Selected",
        eventProperties: { "Select Search Box": true },
      });
    },
  },
};
</script>

<style scoped>
.electorate-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  position: relative;
  top: -250px;

  width: 100%;
}

.search {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  top: 36px;

  width: 600px;
  padding: 12px;

  /* Input style */
  border: 1px solid #cacaca;
  background-color: white;

  scroll-margin-top: 100px;

  z-index: 5;
}

.search input {
  border: none;
  box-shadow: none;
  width: 525px;
  height: 100%;
  margin: 0;
}

.search input::placeholder {
  font-size: 16px;
  font-style: italic;
}

.search .icon {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--black);
  border-radius: 2px;
  width: 48px;
  height: 48px;
  cursor: pointer;
}

.search .icon i {
  color: white;
}

.wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 1250px;
  padding: 24px;
  background-color: white;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
}

.wrapper h4.electorate-name {
  font-family: "WWF",sans-serif;
  font-size: 32px;
  line-height: normal;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  margin: 25px 0 0 0;
}

.content {
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 100%;
}

.details {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.section,
.section .left {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  width: 43%;
}

.section .text p {
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 150%;
  letter-spacing: -0.023em;
}

.section .count .text {
  max-width: 100px;
}

.section .count p {
  font-family: "WWF",sans-serif;
  font-size: 64px;
  font-weight: 400;
  letter-spacing: -0.023em;
}

.section .icon {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 48px;
  min-height: 48px;
}

.section .icon i {
  font-size: 40px;
}

.section.middle {
  display: flex;
  flex-direction: row-reverse;
  gap: 12px;
}

.section.middle .section-content {
  display: flex;
  align-items: center;
  gap: 40px;
}

.section.middle .icon.grade {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--bg-color);
  border-radius: 100px;
}

.section.middle .grade {
  font-family: "WWF";
  font-size: 32px;
  color: #ffffff;
}

.section.btn {
  width: 30%;
}

.info-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1.5px solid #323232;
  border-radius: 100%;
  padding: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.separator {
  display: flex;
  justify-content: center;
  align-items: center;
}

.separator .bar {
  border: 0.5px solid #dadada;
  height: 50%;
}

button {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 200px;
  height: 80px;
  font-family: var(--font-secondary);
  font-size: 1.5em;
  color: #fff;
  background-color: var(--orange);
  border-radius: var(--border-radius);
  cursor: pointer;
}

.learn-more {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0px;
  margin-bottom: -20px;
  cursor: pointer;
}

.learn-more p {
  font-weight: 600;
  font-size: 12px;
  line-height: 150%;
}

.uppercase {
  text-transform: uppercase;
}

@media screen and (max-width: 1250px) {
  .electorate-details {
    justify-content: center;
    height: 310px;
    top: -310px;
  }

  .wrapper {
    gap: 4px;
    width: 100%;
    padding-top: 48px;
  }

  .content {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;
    padding-top: 8px;
  }

  .details {
    justify-content: center;
    gap: 24px;
    width: 100%;
  }

  .separator .bar {
    height: 100%;
  }

  .separator.end {
    display: none;
  }

  .section.btn {
    width: 100%;
  }

  button {
    width: 100%;
    font-size: 40px;
  }
}

@media screen and (max-width: 900px) {
  .electorate-details {
    height: 50%;
    top: -325px;
  }

  .search {
    width: 70%;
  }
}

@media screen and (max-width: 860px) {
  .electorate-details {
    top: -350px;
  }

  .section.species,
  .section.middle .section-content {
    flex-direction: column;
  }

  .section.middle {
    gap: 0;
  }

  .section.middle .icon {
    display: flex;
    align-items: flex-start;
    height: 100%;
  }

  .section.middle .icon .info-icon {
    position: relative;
    right: 240%;
    bottom: 15%;
  }
}

@media screen and (max-width: 800px) {
  .electorate-details {
    top: -400px;
  }

  .section.middle .icon .info-icon {
    right: 215%;
  }
}

@media screen and (max-width: 730px) {
  .electorate-details {
    top: -415px;
  }

  .search {
    justify-content: space-between;
    width: 90%;
  }

  .content {
    justify-content: center;
    gap: 16px;
  }
}

@media screen and (max-width: 700px) {
  .electorate-details {
    height: 37%;
  }

  .content {
    justify-content: center;
    gap: 16px;
  }

  .section.middle .icon .info-icon {
    right: 190%;
  }
}

@media screen and (max-width: 645px) {
  .electorate-details {
    height: 40%;
  }

  .learn-more {
    margin-bottom: -10px;
  }

  .section.middle .icon .info-icon {
    right: 160%;
    bottom: 12px;
  }
}

@media screen and (max-width: 560px) {
  .electorate-details {
    height: 43%;
    top: -390px;
  }

  .search .icon {
    width: 85px;
  }

  .search .icon i {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .fa-search::before {
    font-size: 18px;
  }

  .wrapper {
    gap: 12px;
    padding-top: 30px;
  }

  .details {
    justify-content: space-between;
    gap: 0;
  }

  .section {
    flex-direction: column;
  }

  .section .text p {
    font-size: 14px;
  }

  .section .count p {
    font-size: 42px;
  }

  .section.middle .icon .info-icon {
    right: 125%;
    bottom: 4px;
  }

  .text {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

@media screen and (max-width: 500px) {
  .section.middle .icon .info-icon {
    right: 95%;
  }
}

@media screen and (max-width: 445px) {
  .section.middle .icon .info-icon {
    right: 60%;
  }
}

@media screen and (max-width: 375px) {
  .search {
    width: 85%;
  }

  .section.middle .icon .info-icon {
    right: 50%;
  }
}

/* Elcom Style */
p {
  font-family: 'Lato',sans-serif;
  position: relative;
  margin: 0;
  font-size: 16px;
}

@media only screen and (max-width: 40em) {
  p {
    font-size: 15px;
    /* line-height: 23px; */
  }
}
/* END */
</style>




// WEBPACK FOOTER //
// ElectorateDetails.vue?153a763e